import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import api from '~/services/api';

interface ILoja {
  des_loja: any;
  des_uf: string;
  cod_loja: number;
  des_fantasia: string;
  num_cnpj: string;
  des_cidade: string;
  des_logradouro: string;
  num_endereco: string;
  des_bairro: string;
  num_cep: string;
  cst_pis_cof_entrada: string;
  cst_pis_cof_saida: string;
  cst_pis_cof_saida_aliq_zero: string;
  cst_pis_cof_ent_aliq_zero: string;
  cst_pis_cof_saida_monof: string;
  cst_pis_cof_ent_monof: string;
  cst_pis_cof_saida_subst: string;
  cst_pis_cof_ent_subst: string;
  cst_pis_cof_saida_imune: string;
  cst_pis_cof_ent_imune: string;
  cst_pis_cof_saida_susp: string;
  cst_pis_cof_ent_susp: string;
  tipo_regime: number;
  flg_st_bc_pis_cof: boolean;
  flg_ipi_bc_pis_cof: boolean;
  tipo_integra_frente: number;
  tipo_totalizador_etq_bal: number;
  qtd_dig_cod_bal: number;
  flg_sem_dig_verif_bal: boolean;
  regra_empresa: {
    tipo_margem: number;
    tipo_custo: number;
    per_tol_custo: number;
    per_bloq_custo: number;
    per_tol_venda: number;
    per_bloq_venda: number;
  };
  parametro_nfe: {
    tipo_ambiente: number;
  };
}

export interface IUseLoja {
  codLoja: number;
  loja: ILoja;
  handleLoja: (value: ILoja) => void;
  changeLoja: (cod_loja: number) => void;
}

export default function useLoja(): IUseLoja {
  const queryClient = useQueryClient();
  const [codLoja, setCodLoja] = useState<number>(0);

  const { data: loja } = useQuery(
    ['loja', codLoja],
    async () => {
      if (codLoja > 0) {
        const res = await api.get(`/lojas/${codLoja}`);
        const { data, success } = res.data;
        if (success) return data;

        throw new Error('Erro ao carregar loja');
      }
      return undefined;
    },
    {
      enabled: codLoja > 0,
      keepPreviousData: true,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 10,
    },
  );

  const handleLoja = useCallback(
    (value: ILoja) => {
      queryClient.setQueryData(['loja', codLoja], value);
    },
    [codLoja, queryClient],
  );

  const changeLoja = useCallback((cod_loja: number) => {
    setCodLoja(cod_loja);
  }, []);

  const defaultLoja: ILoja = {
    des_loja: '',
    des_uf: '',
    cod_loja: 0,
    des_fantasia: '',
    num_cnpj: '',
    des_cidade: '',
    des_logradouro: '',
    num_endereco: '',
    des_bairro: '',
    num_cep: '',
    cst_pis_cof_entrada: '',
    cst_pis_cof_saida: '',
    cst_pis_cof_saida_aliq_zero: '',
    cst_pis_cof_ent_aliq_zero: '',
    cst_pis_cof_saida_monof: '',
    cst_pis_cof_ent_monof: '',
    cst_pis_cof_saida_subst: '',
    cst_pis_cof_ent_subst: '',
    cst_pis_cof_saida_imune: '',
    cst_pis_cof_ent_imune: '',
    cst_pis_cof_saida_susp: '',
    cst_pis_cof_ent_susp: '',
    tipo_regime: 0,
    flg_st_bc_pis_cof: false,
    flg_ipi_bc_pis_cof: false,
    tipo_integra_frente: 0,
    tipo_totalizador_etq_bal: 0,
    qtd_dig_cod_bal: 0,
    flg_sem_dig_verif_bal: false,
    regra_empresa: {
      tipo_margem: 0,
      tipo_custo: 0,
      per_tol_custo: 0,
      per_bloq_custo: 0,
      per_tol_venda: 0,
      per_bloq_venda: 0,
    },
    parametro_nfe: {
      tipo_ambiente: 0,
    },
  };

  return {
    codLoja,
    loja: loja ?? defaultLoja,
    handleLoja,
    changeLoja,
  };
}
